import { useEffect, useRef, useState } from "react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { useAuth } from "@/context/AuthContext";
import type { Language } from "@/utils/dictionary";
import { Step1SelectProduct } from "@/feature/ads/cretives/components/modals/steps/Step1SelectProduct";
import { Step2VideoStudio } from "./Step2VideoStudio";
import { useVideoDraftStore } from "@/store/videoDraftStore";

type Step = 1 | 2;

interface CreateVideoModalProps {
  open: boolean;
  onClose: () => void;
  initialProduct?: ProductInfoCreative | null;
}

export function CreateVideoModal({ open, onClose, initialProduct }: CreateVideoModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { language } = useAuth();
  const [step, setStep] = useState<Step>(initialProduct ? 2 : 1);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoCreative | null>(initialProduct ?? null);
  const [studioStage, setStudioStage] = useState<string>("style");

  // Phase 2 fix: block click-outside while a draft is active. The user
  // should leave the preview through explicit actions so we can clear the
  // draft store/localStorage intentionally.
  const draftStoreStatus = useVideoDraftStore((s) => s.storeStatus);
  // Phase 2 V4 (Apr 18 2026) — used to rehydrate a minimal ProductInfoCreative
  // when the user refreshed mid-preview and the modal auto-reopened via
  // AdsPage's resumeFromStorage effect. Step2VideoStudio needs a `product`
  // prop (required) but in draftMode it only renders PreviewApproval,
  // which reads everything from the draft store — so a stub product
  // populated from the draft fields is enough.
  const draftForResume = useVideoDraftStore((s) => s.draft);
  // Only pre-approve draft states should own the modal. Once the user
  // approves, rendering moves to the gallery loading card and should not
  // block creating another video.
  const isDraftActive =
    draftStoreStatus === "directing" ||
    draftStoreStatus === "ready" ||
    draftStoreStatus === "approving" ||
    draftStoreStatus === "rejecting" ||
    draftStoreStatus === "editing_script" ||
    draftStoreStatus === "regenerating_image" ||
    draftStoreStatus === "regenerating_style" ||
    draftStoreStatus === "generating_audio";

  // Phase 2 V9 (Apr 19 2026 late night — BUG FIX) — this effect used to have
  // `isDraftActive` and `draftForResume` in its deps. That caused a nasty
  // regression: when the user clicked "Crear mi video", PreviewApproval
  // called `reset()` on the draft store (flipping isDraftActive → false
  // and draftForResume → null) BEFORE `onDone()` propagated `open=false`.
  // Between those two ticks, this effect re-ran with `open=true` +
  // `isDraftActive=false` + `initialProduct=null`, fell through to the
  // ELSE branch, and called `setStep(1) + setSelectedProduct(null)`,
  // yanking the user back to the product picker.
  //
  // Fix: only run on the `open` false→true transition. A ref tracks the
  // previous value of `open` so the resume logic fires exactly once per
  // modal open, and subsequent draft-store changes don't reset the step.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    // Already open — ignore. This is what prevents the reset-triggered
    // step=1 snap back to product picker during the approve flow.
    if (prevOpenRef.current) return;
    prevOpenRef.current = true;

    // Phase 2 V4 — prioritize resuming an active draft over the wizard.
    // If we opened because AdsPage detected a draft in storage, skip
    // straight to step 2 with a synthetic product built from the draft,
    // so the user lands on the preview they already paid for instead
    // of being asked to pick a product again.
    if (isDraftActive && draftForResume) {
      setStep(2);
      setSelectedProduct({
        product_id: draftForResume.product_id ?? "",
        website_id: "",
        name: draftForResume.product_name ?? "",
        description: "",
        image_url: draftForResume.product_image_url ?? undefined,
      });
      return;
    }
    if (initialProduct) {
      setStep(2);
      setSelectedProduct(initialProduct);
    } else {
      setStep(1);
      setSelectedProduct(null);
    }
  }, [open, initialProduct, isDraftActive, draftForResume]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // While a draft is in flight or waiting for approval, ignore outside clicks.
      // The user must use the explicit Cancel / Approve buttons (or the X) so
      // they can't accidentally lose 194 credits worth of progress.
      if (isDraftActive) return;

      const target = event.target as Node;
      if (!modalRef.current?.contains(target)) {
        const isInsideNestedModal =
          target instanceof Element && target.closest("[data-fluxi-nested-modal]");
        if (!isInsideNestedModal) {
          onClose();
        }
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, isDraftActive]);

  if (!open) return null;

  // Phase 5.4: cuando el draft está corriendo (director Gemini o regenerate
  // style), achicamos el modal entero para no mostrar una pantalla gigante
  // con un loader chiquito en el medio. Mismo patrón que el onboarding del
  // funnel cuando está procesando.
  const isLoadingDraft =
    step === 2 &&
    (draftStoreStatus === "directing" ||
      draftStoreStatus === "regenerating_style");
  const isStyleStage = step === 2 && studioStage === "style" && !isLoadingDraft && !isDraftActive;
  const isAngleStage = step === 2 && studioStage === "angle" && !isLoadingDraft;
  const isAvatarStage = step === 2 && studioStage === "avatar" && !isLoadingDraft && !isDraftActive;

  // Estilos inline para tener interpolación suave entre tamaños. Tailwind
  // max-w-* no anima entre clases distintas, así que usamos width directo.
  // IMPORTANTE: ambos states usan height fijo en px — CSS no puede animar
  // entre `auto` y un valor concreto, y eso causaba el "salto" de la
  // transición entre el loader y el preview.
  const sizeStyle: React.CSSProperties =
    step === 2 && !isLoadingDraft && isDraftActive
      ? {
          width: "min(95vw, 1024px)",
          height: "min(90vh, 860px)",
        }
      : isAngleStage
      ? {
          width: "min(90vw, 760px)",
          height: "min(88vh, 470px)",
        }
      : isStyleStage
      ? {
          width: "min(90vw, 560px)",
          height: "min(86vh, 430px)",
        }
      : isAvatarStage
      ? {
          width: "min(78vw, 820px)",
          height: "min(84vh, 760px)",
        }
      : step === 2 && !isLoadingDraft
      ? {
          width: "min(92vw, 860px)",
          height: "min(88vh, 580px)",
        }
      : step === 2 && isLoadingDraft
      ? {
          // Loading state del draft: height fijo para que la transición
          // a la vista grande del preview pueda interpolar (CSS no anima
          // entre auto y px).
          width: "min(95vw, 480px)",
          height: "min(90vh, 420px)",
        }
      : {
          // Step 1 (selección de producto): height auto, el contenido
          // decide su propio alto.
          width: "min(95vw, 480px)",
          height: "auto",
        };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div
        ref={modalRef}
        className="relative bg-background rounded-2xl shadow-2xl overflow-hidden border border-border/50 transition-[width,height] duration-500 ease-out"
        style={sizeStyle}
      >
        {step === 1 && (
          <Step1SelectProduct
            language={language as Language}
            onClose={onClose}
            onSelectProduct={(product) => {
              setSelectedProduct(product);
              setStep(2);
            }}
          />
        )}
        {step === 2 && selectedProduct && (
          <Step2VideoStudio
            product={selectedProduct}
            onClose={onClose}
            onStageChange={setStudioStage}
          />
        )}
      </div>
    </div>
  );
}
